import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTours = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setTours(data || []);
    } catch (err) {
      console.error('Error fetching tours:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTours();
    
    // Подписываемся на изменения в таблице tours
    const subscription = supabase
      .channel('tours-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'tours' }, 
        () => {
          console.log('Tours table changed, refreshing...');
          fetchTours();
        }
      )
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const deleteTour = async (id, title) => {
    if (!window.confirm(`Удалить тур "${title}"? Это действие нельзя отменить.`)) return;
    
    try {
      // Сначала получаем тур, чтобы удалить изображения из Storage
      const { data: tour, error: fetchError } = await supabase
        .from('tours')
        .select('image_url, gallery_images')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Удаляем главное изображение
      if (tour.image_url) {
        const imagePath = tour.image_url.split('/').pop();
        if (imagePath) {
          await supabase.storage
            .from('tour-images')
            .remove([`main/${imagePath}`]);
        }
      }
      
      // Удаляем изображения из галереи
      if (tour.gallery_images && tour.gallery_images.length > 0) {
        for (const url of tour.gallery_images) {
          const imagePath = url.split('/').pop();
          if (imagePath) {
            await supabase.storage
              .from('tour-images')
              .remove([`gallery/${imagePath}`]);
          }
        }
      }
      
      // Удаляем тур из базы
      const { error: deleteError } = await supabase
        .from('tours')
        .delete()
        .eq('id', id);
      
      if (deleteError) throw deleteError;
      
      alert('Тур успешно удален');
    } catch (err) {
      console.error('Error deleting tour:', err);
      alert('Ошибка при удалении тура: ' + err.message);
    }
  };

  const archiveTour = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'archived' : 'active';
    const action = newStatus === 'active' ? 'восстановлен' : 'архивирован';
    
    if (!window.confirm(`Вы уверены, что хотите ${action} этот тур?`)) return;
    
    try {
      const { error } = await supabase
        .from('tours')
        .update({ status: newStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      alert(`Тур успешно ${action}`);
    } catch (err) {
      console.error('Error updating tour status:', err);
      alert('Ошибка при изменении статуса тура: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return {
          text: 'Активен',
          style: {
            backgroundColor: '#d1fae5',
            color: '#065f46',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '500'
          }
        };
      case 'archived':
        return {
          text: 'В архиве',
          style: {
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '500'
          }
        };
      default:
        return { text: status, style: {} };
    }
  };

  if (loading && tours.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        <div>Загрузка туров...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        color: '#dc2626',
        backgroundColor: '#fee2e2',
        borderRadius: '0.5rem',
        margin: '1rem'
      }}>
        <p>Ошибка загрузки туров: {error}</p>
        <button 
          onClick={fetchTours}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer'
          }}
        >
          Повторить попытку
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
          Туры в Китай
          <span style={{ 
            fontSize: '0.875rem', 
            color: '#6b7280', 
            fontWeight: 'normal',
            marginLeft: '0.5rem'
          }}>
            ({tours.length} {tours.length === 1 ? 'тур' : tours.length < 5 ? 'тура' : 'туров'})
          </span>
        </h1>
        <Link
          to="/admin/tours/new"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '0.375rem',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
        >
          <span style={{ fontSize: '1.25rem' }}>+</span>
          Добавить тур
        </Link>
      </div>
      
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflowX: 'auto'
      }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          minWidth: '800px'
        }}>
          <thead style={{ backgroundColor: '#f9fafb' }}>
            <tr>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Изображение
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Название
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Метка
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'left', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Статус
              </th>
              <th style={{ 
                padding: '0.75rem 1rem', 
                textAlign: 'right', 
                fontSize: '0.75rem', 
                fontWeight: '500', 
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Действия
              </th>
            </tr>
          </thead>
          <tbody style={{ borderTop: '1px solid #e5e7eb' }}>
            {tours.map((tour) => {
              const statusBadge = getStatusBadge(tour.status);
              return (
                <tr key={tour.id} style={{ borderBottom: '1px solid #e5e7eb', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <img
                      src={tour.image_url || 'https://via.placeholder.com/64x64?text=No+image'}
                      alt={tour.title}
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        objectFit: 'cover', 
                        borderRadius: '0.375rem',
                        backgroundColor: '#f3f4f6'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/64x64?text=Error';
                      }}
                    />
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>
                    {tour.title}
                    {tour.slug && (
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                        slug: {tour.slug}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {tour.mark && (
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#fed7aa',
                        color: '#9b2c1d',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {tour.mark}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={statusBadge.style}>
                      {statusBadge.text}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <Link
                        to={`/admin/tours/${tour.id}`}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        ✏️ Редактировать
                      </Link>
                      <button
                        onClick={() => archiveTour(tour.id, tour.status)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: tour.status === 'active' ? '#f59e0b' : '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = tour.status === 'active' ? '#d97706' : '#059669'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = tour.status === 'active' ? '#f59e0b' : '#10b981'}
                      >
                        {tour.status === 'active' ? '📦 В архив' : '🔄 Восстановить'}
                      </button>
                      <button
                        onClick={() => deleteTour(tour.id, tour.title)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                      >
                        🗑️ Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {tours.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            color: '#6b7280',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✈️</div>
            <p>Нет добавленных туров</p>
            <Link
              to="/admin/tours/new"
              style={{
                display: 'inline-block',
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.375rem'
              }}
            >
              Добавить первый тур
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tours;